import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next';

// Максимальная высота выпадающего меню по умолчанию — совпадает с .custom-select-menu
// в style.css. Инлайновый max-height из positionMenu() перебивает css, поэтому селект
// со своим menuMaxHeight раскрывается выше без правки стилей
const MENU_MAX_HEIGHT = 200
// Штрих-коды: EAN-8, UPC-A, EAN-13, ITF-14 — только цифры
const BARCODE_REGEX = /^\d{8,14}$/
// Сканер «печатает» быстрее человека: средний интервал между символами меньше этого
const SCANNER_KEY_INTERVAL = 50
// Сканер без суффикса Enter: код считаем введённым после такой паузы
const SCANNER_IDLE_TIMEOUT = 150

function ReactCustomSelect({
	table,
	value,
	scrollRef,
	options,
	index,
	search,
	onKeyUp,
	selectItem,
	onClose,
	tabIndex,
	uniqueId = 'select',
	menuMinWidth = 0, // Минимальная ширина выпадающего списка, если колонка узкая
	menuMaxHeight = MENU_MAX_HEIGHT, // Высота выпадающего списка, если строк много
	isDisabled = false,
	isClearable = false,
	handleClearSelect, // Поиск по баркоду
	handleSelectSearch, // Поиск по баркоду
	isBarcodeSearchEnabled, // Поиск по баркоду
}) {
	const { t } = useTranslation()

	const wrapperRef = useRef(null);
	const inputRef = useRef(null);

	const [showMenu, setShowMenu] = useState(false)
	const [searchValue, setSearchValue] = useState('')

	// Замер скорости ввода: с какого момента набирается текущее значение
	const typingStartRef = useRef(0)
	const scanTimerRef = useRef(null)
	// Enter скана уже обработан на keydown — его keyup не должен выбрать позицию из списка
	const skipEnterKeyUpRef = useRef(false)

	const closeReactSelect = useCallback(() => {
		clearTimeout(scanTimerRef.current)
		setSearchValue('')
		setShowMenu(false)
		if (onClose) onClose()
	}, [onClose]);

	function submitBarcode(value) {
		clearTimeout(scanTimerRef.current)
		handleSelectSearch(value, true)
		// Поле чистим сразу: если скан досчитал другую строку, value этой не меняется
		// и сама она не закроется — следующий код дописался бы к старому
		closeReactSelect()
	}

	// Enter ловим на keydown и берём значение из самого инпута: сканер шлёт символы
	// пачкой, и к keyup состояние searchValue может ещё не догнать DOM
	function onKeyDownMiddleWare(e) {
		skipEnterKeyUpRef.current = false
		if (e.keyCode !== 13 || !isBarcodeSearchEnabled || typeof handleSelectSearch !== 'function') return

		const value = e.target.value.trim()
		if (BARCODE_REGEX.test(value)) {
			e.preventDefault()
			skipEnterKeyUpRef.current = true
			submitBarcode(value)
		}
	}

	// Сканер, настроенный без Enter: код из цифр, набранный быстрее человека,
	// после короткой паузы отправляем сами
	function watchScannerInput(value) {
		clearTimeout(scanTimerRef.current)
		if (!isBarcodeSearchEnabled || typeof handleSelectSearch !== 'function') return

		const now = Date.now()
		if (value.length <= 1) typingStartRef.current = now
		if (!BARCODE_REGEX.test(value)) return

		const averageInterval = (now - typingStartRef.current) / (value.length - 1)
		if (averageInterval > SCANNER_KEY_INTERVAL) return

		scanTimerRef.current = setTimeout(() => {
			const currentValue = (inputRef.current?.value ?? '').trim()
			if (currentValue === value) submitBarcode(value)
		}, SCANNER_IDLE_TIMEOUT)
	}

	function onKeyUpMiddleWare(e) {
		if (e.keyCode === 13 && skipEnterKeyUpRef.current) {
			skipEnterKeyUpRef.current = false
			return
		}

		if (showMenu) {
			if (e.keyCode === 27) {
				closeReactSelect()
			}

			if (e.keyCode === 13) {
				closeReactSelect()
				if (typeof selectItem === 'function') {
					selectItem(options.find(el => el.selected))
				}
			}

			onKeyUp(e)
		}
	}

	// Меню с position: fixed — координаты и ширину считаем от инпута.
	// Если меню шире колонки, сдвигаем влево, чтобы не уехало за край экрана.
	// Если снизу не хватает места (последние строки таблицы) — раскрываем вверх.
	function positionMenu() {
		const selectMenu = document.getElementById(`menu-${uniqueId}-${index}`);
		const parentContainer = document.getElementById(`input-${uniqueId}-${index + 1}`);
		if (!selectMenu || !parentContainer) return
		const parentRect = parentContainer.getBoundingClientRect();
		const menuWidth = Math.max(parentContainer.offsetWidth, menuMinWidth)
		const menuLeft = Math.max(Math.min(parentRect.left, window.innerWidth - menuWidth - 8), 8)

		const gap = 8
		const desiredHeight = Math.min(selectMenu.scrollHeight, menuMaxHeight)
		const spaceBelow = window.innerHeight - parentRect.bottom - gap
		const spaceAbove = parentRect.top - gap
		const openUp = spaceBelow < desiredHeight && spaceAbove > spaceBelow
		const menuHeight = Math.min(desiredHeight, openUp ? spaceAbove : spaceBelow)

		selectMenu.style.maxHeight = `${menuHeight}px`;
		selectMenu.style.top = openUp
			? `${parentRect.top - menuHeight}px`
			: `${parentRect.bottom}px`;
		selectMenu.style.left = `${menuLeft}px`;
		selectMenu.style.width = `${menuWidth}px`;
	}

	function searchMiddleWare(value) {
		search(value)
		setSearchValue(value)
		setShowMenu(!!value)
		watchScannerInput(value.trim())

		setTimeout(() => positionMenu(), 10);
	}

	function handleShowSelect(bool) {
		setShowMenu(bool)

		setTimeout(() => positionMenu(), 10);
	}

	function openReactSelect() {
		inputRef?.current?.focus()
	}

	function returnStyleCustomSelectMenu() {
		const selectMenu = document.getElementById(`input-${uniqueId}-${index + 1}`);
		if (selectMenu) {
			return { 'width': `${Math.max(selectMenu.getBoundingClientRect().width, menuMinWidth)}px` }
		}
	}

	useEffect(() => {
		closeReactSelect()
	}, [value]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		return () => clearTimeout(scanTimerRef.current)
	}, [])

	// Содержимое меню сменилось (список ↔ «Список пустой») — высоту считаем заново,
	// иначе остаётся прежний max-height и в пустом меню появляется скролл
	const hasOptions = !!options?.length
	useEffect(() => {
		if (showMenu) positionMenu()
	}, [hasOptions]); // eslint-disable-line react-hooks/exhaustive-deps

	// Меню с position: fixed само не едет за инпутом — пересчитываем координаты,
	// пока оно открыто (важно для таблиц со своим скроллом).
	useEffect(() => {
		if (!showMenu) return

		function reposition() {
			positionMenu()
		}

		window.addEventListener('scroll', reposition, true);
		window.addEventListener('resize', reposition);

		return () => {
			window.removeEventListener('scroll', reposition, true);
			window.removeEventListener('resize', reposition);
		};
	}, [showMenu]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		function handleClickOutside(event) {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target) && closeReactSelect) {
				closeReactSelect();
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [wrapperRef, closeReactSelect]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="custom-select" ref={wrapperRef}>
			<div className="custom-select-field"
				onClick={() => openReactSelect()}>
				<span className="value nowrap">
					{value}
				</span>
				<input
					tabIndex={tabIndex}
					disabled={isDisabled}
					id={`input-${uniqueId}-${index + 1}`}
					type="text"
					ref={inputRef}
					className={`${showMenu ? 'active' : ''}`}
					value={searchValue ?? ''}
					onChange={(e) => searchMiddleWare(e.target.value)}
					size={searchValue.length}
					onKeyDown={(e) => onKeyDownMiddleWare(e)}
					onKeyUp={(e) => onKeyUpMiddleWare(e)}
					autoComplete="off"
				/>
				{(isClearable && value) &&
					<div className="icon-times text-danger"
						onClick={() => handleClearSelect(index)}>
						<i className="uil uil-times"></i>
					</div>
				}
				<div className="icon"
					onClick={() => handleShowSelect(!showMenu)}>
					<i className="uil uil-angle-down"></i>
				</div>
			</div>

			{showMenu &&
				<div className={`custom-select-menu ${showMenu ? 'active' : ''} ${hasOptions ? '' : 'is-empty'}`}
					style={returnStyleCustomSelectMenu(index)}
					id={`menu-${uniqueId}-${index}`}
					ref={scrollRef}>
					{hasOptions ?
						// table может приходить функцией — тогда список опций строится
						// только при открытом меню, а не для каждой строки таблицы
						<div>{typeof table === 'function' ? table() : table}</div>
						:
						<h6 className="text-center py-3 mb-0">
							{t('list_empty')}
						</h6>
					}
				</div>
			}
		</div>
	)
}

export default ReactCustomSelect